'use client'

import {
  Button,
  Container,
  MantineThemeOverride,
  Notification,
  Popover,
  Portal,
  rem,
  SegmentedControl,
  TextInput,
} from '@mantine/core'
import { defaultColors } from './default-colors'
import buttonClasses from './default-theme.button.module.css'

const DEFAULT_FONT_FAMILY =
  'Lato, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji'
const HEADING_FONT_FAMILY = `Crimson Pro, ${DEFAULT_FONT_FAMILY}`

// Original values: https://github.com/mantinedev/mantine/blob/7dc4a72ece31650761c8aae96756c10e628dc9c7/src/mantine-core/src/components/Container/Container.module.css
const CONTAINER_SIZES: Record<string, string> = {
  xs: rem(576),
  sm: rem(768),
  md: rem(992),
  lg: rem(1200),
  xl: rem(1400),
}

export const defaultTheme: MantineThemeOverride = {
  scale: 1,
  fontSmoothing: true,
  focusRing: 'auto' as const,
  white: '#fff',
  black: '#232F3B',
  colors: defaultColors,
  primaryShade: { light: 6 as const, dark: 8 as const },
  primaryColor: 'brand-blue' as const,
  // variantColorResolver: defaultVariantColorsResolver,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
  respectReducedMotion: false,
  cursorType: 'default' as const,
  defaultGradient: { from: 'blue', to: 'cyan', deg: 45 },
  defaultRadius: 'sm' as const,
  activeClassName: 'mantine-active' as const,
  focusClassName: '',

  headings: {
    fontFamily: HEADING_FONT_FAMILY,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: rem(36), lineHeight: '1.3', fontWeight: '500' },
      h2: { fontSize: rem(30), lineHeight: '1.35' },
      h3: { fontSize: rem(20), lineHeight: '1.4' },
      h4: { fontSize: rem(18), lineHeight: '1.45' },
      h5: { fontSize: rem(16), lineHeight: '1.5' },
      h6: { fontSize: rem(14), lineHeight: '1.5' /*, fontFamily: DEFAULT_FONT_FAMILY */ },
    },
  },

  fontWeights: {
    // Mantine v9 added this token and rewrote the component stylesheets to read
    // `var(--mantine-font-weight-medium)` instead of hard-coding a weight. That
    // folded two different v8 weights into one: Input labels, SegmentedControl,
    // Menu, Notification, Stepper and Timeline were 500, while Button,
    // ActionIcon and Switch were 600. The token defaults to 600, so the first
    // group would silently get heavier. Pin it to 500 to keep our typography.
    // The 600 group is unaffected here — Button carries our own `fw: 700`
    // default below, and ActionIcon/Switch render no text we weight.
    medium: '500',
  },

  fontSizes: {
    xxs: rem(10),
    xs: rem(13),
    sm: rem(14),
    md: rem(16),
    lg: rem(20),
    xl: rem(24),
  },

  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6',
    xl: '1.65',
    xxl: '1.875',
  },

  radius: {
    xs: rem(2),
    sm: rem(4),
    md: rem(8),
    lg: rem(16),
    xl: rem(32),
  },

  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(20),
    xl: rem(24),
    xxl: rem(28),
    // Steps above Mantine's default range, on the same 4px grid. Card insets and section rhythm
    // used to be written as raw rem values or fractional multiples of xxl because nothing larger
    // existed (see docs/design-token-audit.md §3.2).
    xxxl: rem(36),
    section: rem(48),
  },

  breakpoints: {
    xs: '36em',
    sm: '48em', // Note: 48em is used in the terrace/courtyard2 constants.ts file as the predefined mobile breakpoint. If we change this value, we should update the terrace/courtyard2 constants file as well.
    md: '62em',
    lg: '75em',
    xl: '88em',
  },

  // prettier-ignore - prevent prettier from breaking lines in this block
  shadows: {
    xs: `0 ${rem(1)} ${rem(3)} rgba(0, 0, 0, 0.05), 0 ${rem(1)} ${rem(2)} rgba(0, 0, 0, 0.1)`,
    sm: `0 ${rem(1)} ${rem(3)} rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 ${rem(10)} ${rem(15)} ${rem(
      -5
    )}, rgba(0, 0, 0, 0.04) 0 ${rem(7)} ${rem(7)} ${rem(-5)}`,
    md: `0 ${rem(1)} ${rem(3)} rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 ${rem(20)} ${rem(25)} ${rem(
      -5
    )}, rgba(0, 0, 0, 0.04) 0 ${rem(10)} ${rem(10)} ${rem(-5)}`,
    lg: `0 ${rem(1)} ${rem(3)} rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 ${rem(28)} ${rem(23)} ${rem(
      -7
    )}, rgba(0, 0, 0, 0.04) 0 ${rem(12)} ${rem(12)} ${rem(-7)}`,
    xl: `0 ${rem(1)} ${rem(3)} rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 ${rem(36)} ${rem(28)} ${rem(
      -7
    )}, rgba(0, 0, 0, 0.04) 0 ${rem(17)} ${rem(17)} ${rem(-7)}`,
  },

  other: {
    colors: {
      lighterTan: '#FEF7E9',
      lightTan: '#FEF3DE',
      tan: '#E9C06F',
    },
  },
  components: {
    Button: Button.extend({
      defaultProps: {
        size: 'md',
        radius: 'md',
        fw: 700,
      },
      classNames: buttonClasses,
    }),
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          '--container-size': fluid
            ? '100%'
            : size !== undefined && size in CONTAINER_SIZES
              ? CONTAINER_SIZES[size]
              : rem(size),
        },
      }),
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        radius: 'sm',
      },
    }),
    Portal: Portal.extend({
      defaultProps: {
        // Disable reuseTargetNode by default to maintain v7 behavior
        reuseTargetNode: false,
      },
    }),
    Popover: Popover.extend({
      defaultProps: {
        // Disable hideDetached by default to maintain v7 behavior
        hideDetached: false,
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      styles: {
        label: {
          // Override Mantine's default gray-7 with dark-6 for better contrast
          color: 'var(--mantine-color-dark-6)',
        },
      },
    }),
    Notification: Notification.extend({
      styles: {
        description: {
          // Override default dimmed color with darker text for better readability
          color: 'var(--mantine-color-dark-6)',
        },
      },
    }),
  },
}
